import React, { useState, useEffect } from 'react';
import { Bell, Plus, Download, Upload, Play, Pause, Clock, CheckCircle, AlertTriangle, Settings } from 'lucide-react';
import { RBACWrapper } from '../OnboardingHub/RBACWrapper';

interface NotificationRule {
  id: string;
  name: string;
  trigger: string;
  channels: string[];
  enabled: boolean;
  schedule: string;
  recipients: string[];
  lastFired?: string;
  status: 'active' | 'paused' | 'error';
  description?: string;
}

export const NotificationsAdminPanel: React.FC = () => {
  const [rules, setRules] = useState<NotificationRule[]>([
    {
      id: 'rule-001',
      name: 'Task Due Soon Reminder',
      trigger: 'task_due_soon',
      channels: ['email', 'sms'],
      enabled: true,
      schedule: '24h before due',
      recipients: ['client@example.com', '+1234567890'],
      lastFired: '2 hours ago',
      status: 'active',
      description: 'Automated reminder sent 24 hours before task due date'
    },
    {
      id: 'rule-002',
      name: 'Overdue Task Alert',
      trigger: 'task_overdue',
      channels: ['email', 'slack'],
      enabled: true,
      schedule: 'Daily at 9 AM',
      recipients: ['pm@tyler.com', '#onboarding-alerts'],
      lastFired: '1 day ago',
      status: 'active',
      description: 'Daily alert for overdue tasks sent to project managers'
    },
    {
      id: 'rule-003',
      name: 'Client Inactivity Warning',
      trigger: 'client_inactive',
      channels: ['email'],
      enabled: false,
      schedule: 'After 5 days inactive',
      recipients: ['client@example.com', 'pm@tyler.com'],
      status: 'paused',
      description: 'Warning sent when client has been inactive for 5+ days'
    }
  ]);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<NotificationRule | null>(null);

  const handleCreateRule = () => {
    setShowCreateModal(true);
  };

  const handleImportTemplates = async () => {
    try {
      const response = await fetch('/api/notifications/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Templates imported successfully:', result);
      } else {
        console.error('Failed to import templates');
      }
    } catch (error) {
      console.error('Error importing templates:', error);
    }
  };

  const handleExportSettings = async () => {
    try {
      const response = await fetch('/api/notifications/export', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const settings = await response.json();
        const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'notification-settings.json';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        console.error('Failed to export settings');
      }
    } catch (error) {
      console.error('Error exporting settings:', error);
    }
  };

  const toggleRule = async (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;

    try {
      const response = await fetch(`/api/notifications/rules/${ruleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ enabled: !rule.enabled })
      });

      if (response.ok) {
        setRules(rules.map(r => 
          r.id === ruleId ? { ...r, enabled: !r.enabled, status: !r.enabled ? 'active' : 'paused' } : r
        ));
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const testRule = async (ruleId: string) => {
    try {
      const response = await fetch(`/api/notifications/rules/${ruleId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        console.log('Test notification sent successfully');
      }
    } catch (error) {
      console.error('Error testing rule:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'paused': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'error': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'paused': return <Pause className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <RBACWrapper userRole="pm" requiredPermission="notifications" requiredAction="admin">
      <div className="h-full bg-white">
        <div className="p-8">
          <div className="mb-8">
            <h2 className="tyler-h2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
              Notification Rules Administration
            </h2>
            <p className="text-gray-600 text-lg">
              Manage automation rules, templates, and notification policies for onboarding workflows
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <button
              onClick={handleCreateRule}
              className="flex items-center justify-center p-6 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-150 group"
            >
              <Plus className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Create New Rule</span>
            </button>
            
            <button
              onClick={handleImportTemplates}
              className="flex items-center justify-center p-6 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-[#4267A1] transition-all duration-150 group"
            >
              <Upload className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Import Templates</span>
            </button>
            
            <button
              onClick={handleExportSettings}
              className="flex items-center justify-center p-6 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-[#4267A1] transition-all duration-150 group"
            >
              <Download className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Export Settings</span>
            </button>
          </div>

          <div className="tyler-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Bell className="w-6 h-6 text-[#4267A1] mr-3" />
                Active Rules ({rules.filter(r => r.enabled).length} of {rules.length})
              </h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Environment:</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  Production
                </span>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Rule Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Trigger</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Channels</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Schedule</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Last Fired</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{rule.name}</div>
                          {rule.description && (
                            <div className="text-sm text-gray-500 mt-1">{rule.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {rule.trigger.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-wrap gap-1">
                          {rule.channels.map(channel => (
                            <span key={channel} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                              {channel.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{rule.schedule}</td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(rule.status)}`}>
                          {getStatusIcon(rule.status)}
                          <span className="ml-1 capitalize">{rule.status}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{rule.lastFired || 'Never'}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => testRule(rule.id)}
                            className="p-2 text-gray-600 hover:text-[#4267A1] hover:bg-blue-50 rounded-lg transition-all duration-150"
                            title="Test rule"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleRule(rule.id)}
                            className={`p-2 rounded-lg transition-all duration-150 ${
                              rule.enabled 
                                ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50' 
                                : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                            }`}
                            title={rule.enabled ? 'Pause rule' : 'Enable rule'}
                          >
                            {rule.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setSelectedRule(rule)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-150"
                            title="Edit rule"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="tyler-card p-4 text-center">
              <div className="text-2xl font-bold text-[#4267A1] mb-1">
                {rules.filter(r => r.enabled).length}
              </div>
              <div className="text-sm text-gray-600">Active Rules</div>
            </div>
            <div className="tyler-card p-4 text-center">
              <div className="text-2xl font-bold text-[#8A9150] mb-1">
                {rules.filter(r => r.lastFired).length}
              </div>
              <div className="text-sm text-gray-600">Rules Fired Today</div>
            </div>
            <div className="tyler-card p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600 mb-1">
                {rules.filter(r => r.status === 'paused').length}
              </div>
              <div className="text-sm text-gray-600">Paused Rules</div>
            </div>
            <div className="tyler-card p-4 text-center">
              <div className="text-2xl font-bold text-red-600 mb-1">
                {rules.filter(r => r.status === 'error').length}
              </div>
              <div className="text-sm text-gray-600">Error Rules</div>
            </div>
          </div>
        </div>
      </div>
    </RBACWrapper>
  );
};
